/**
 * @module manifest/visibility/evaluateVisibility
 * @description 配置项显隐评估 + formDelKey 计算（纯函数，不依赖 React）
 */

import type { LayoutGroupWithVisibility } from '../panelLayout/types';
import CONFIG_ITEM_UPLOAD_KEYS, {
  CONFIG_GROUP_ITEM_UPLOAD_KEYS,
} from '../upload-keys';
import type { QBStateSnapshot } from '../../types';

export interface VisibilityResult {
  groups: Record<string, boolean>;
  items: Record<string, Record<string, boolean>>;
  formDelKey: Record<string, boolean>;
}

/**
 * 收集当前 QB 下仍应参与上传的字段名（任一可见子项声明即保留）
 *
 * 条形图 X 轴 TopN 隐藏、Y 轴 TopN 可见时共用 count/dynamicState/showOthers，
 * 不可因 X 轴子项隐藏就把 Y 轴 TopN 字段写入 formDelKey。
 */
function collectActiveUploadKeys(
  qbSnapshot: QBStateSnapshot,
  groups: LayoutGroupWithVisibility[],
): Set<string> {
  const activeKeys = new Set<string>();

  for (const group of groups) {
    const isVisible = !group.visible || group.visible(qbSnapshot);
    if (!isVisible) {
      continue;
    }

    if (!group.itemsVisibility) {
      const keys = CONFIG_ITEM_UPLOAD_KEYS[group.id];
      if (keys) {
        for (const k of keys) {
          activeKeys.add(k);
        }
      }
      continue;
    }

    for (const [itemId, fn] of Object.entries(group.itemsVisibility)) {
      if (!fn(qbSnapshot)) {
        continue;
      }
      const itemKeys = CONFIG_GROUP_ITEM_UPLOAD_KEYS[group.id]?.[itemId];
      if (itemKeys) {
        for (const k of itemKeys) {
          activeKeys.add(k);
        }
      }
    }
  }

  return activeKeys;
}

/**
 * 评估显隐 + 计算 formDelKey
 * formDelKey: 不可见 group/子项的上传字段；若其它可见子项仍声明同名字段则不过滤
 */
export function evaluateVisibility(
  qbSnapshot: QBStateSnapshot,
  groups: LayoutGroupWithVisibility[],
): VisibilityResult {
  const groupVis: Record<string, boolean> = {};
  const itemVis: Record<string, Record<string, boolean>> = {};
  const delKeys: Record<string, boolean> = {};
  const activeKeys = collectActiveUploadKeys(qbSnapshot, groups);

  const markDel = (keys: string[] | undefined): void => {
    if (!keys) {
      return;
    }
    for (const k of keys) {
      if (!activeKeys.has(k)) {
        delKeys[k] = true;
      }
    }
  };

  for (const group of groups) {
    const isVisible = !group.visible || group.visible(qbSnapshot);
    groupVis[group.id] = isVisible;

    if (!isVisible) {
      markDel(CONFIG_ITEM_UPLOAD_KEYS[group.id]);
    }

    if (group.itemsVisibility) {
      const itemsResult: Record<string, boolean> = {};
      for (const [itemId, fn] of Object.entries(group.itemsVisibility)) {
        const itemVisible = isVisible && fn(qbSnapshot);
        itemsResult[itemId] = itemVisible;
        if (!itemVisible) {
          markDel(CONFIG_GROUP_ITEM_UPLOAD_KEYS[group.id]?.[itemId]);
        }
      }
      itemVis[group.id] = itemsResult;
    }
  }

  return { groups: groupVis, items: itemVis, formDelKey: delKeys };
}
